import { create } from 'zustand';
import { api, getAccessToken, type UploadInitResponse } from '../lib/api';
import toast from 'react-hot-toast';

export interface UploadTask {
  id: string;
  file: File;
  parentId: string | null;
  progress: number;
  status: 'pending' | 'uploading' | 'paused' | 'completed' | 'error';
  error?: string;
  speed?: number; // bytes/s
}

const CONCURRENCY = 4;
const RESUME_DB = 'netdisk-upload-resume';

interface ResumeRecord {
  id: string;
  nodeId: string;
  uploadId: string;
  fileName: string;
  fileSize: number;
  lastModified: number;
  parts: Record<number, string>; // partNumber -> etag
}

// —— IndexedDB 断点续传记录 ——
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(RESUME_DB, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('records')) db.createObjectStore('records', { keyPath: 'id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function saveResume(rec: ResumeRecord) {
  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction('records', 'readwrite');
      tx.objectStore('records').put(rec);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch { /* ignore */ }
}

async function loadResume(id: string): Promise<ResumeRecord | null> {
  try {
    const db = await openDB();
    const rec = await new Promise<ResumeRecord | null>((resolve, reject) => {
      const tx = db.transaction('records', 'readonly');
      const req = tx.objectStore('records').get(id);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
    db.close();
    return rec;
  } catch {
    return null;
  }
}

async function clearResume(id: string) {
  try {
    const db = await openDB();
    await new Promise<void>((resolve) => {
      const tx = db.transaction('records', 'readwrite');
      tx.objectStore('records').delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
    db.close();
  } catch { /* ignore */ }
}

function putPart(url: string, blob: Blob, onProgress?: (loaded: number) => void): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable && onProgress) onProgress(e.loaded); };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const etag = xhr.getResponseHeader('ETag') || xhr.getResponseHeader('etag') || '';
        resolve(etag.replace(/^"|"$/g, ''));
      } else {
        reject(new Error(`分片上传失败 (${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error('网络错误'));
    xhr.send(blob);
  });
}

async function uploadWithInit(
  task: UploadTask,
  init: UploadInitResponse,
  onProgress: (pct: number, speed: number) => void
): Promise<void> {
  const { file } = task;

  // 秒传或已存在
  if (init.instant || init.mode === undefined) {
    onProgress(100, 0);
    return;
  }

  // 代理回退模式
  if (init.mode === 'proxy') {
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', init.uploadUrl!);
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      const token = getAccessToken();
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      let last = 0, lastT = Date.now();
      xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable) return;
        const now = Date.now();
        if (now - lastT > 500) {
          const speed = (e.loaded - last) / ((now - lastT) / 1000);
          last = e.loaded; lastT = now;
          onProgress(Math.round((e.loaded / e.total) * 100), speed);
        }
      };
      xhr.onload = () => { if (xhr.status >= 200 && xhr.status < 300) resolve(); else reject(new Error(`上传失败 (${xhr.status})`)); };
      xhr.onerror = () => reject(new Error('网络错误'));
      xhr.send(file);
    });
    onProgress(100, 0);
    return;
  }

  // 小文件单次直传
  if (init.mode === 'single') {
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', init.uploadUrl!);
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100), 0); };
      xhr.onload = () => { if (xhr.status >= 200 && xhr.status < 300) resolve(); else reject(new Error(`上传失败 (${xhr.status})`)); };
      xhr.onerror = () => reject(new Error('网络错误'));
      xhr.send(file);
    });
    await api.upload.complete({ nodeId: init.nodeId });
    return;
  }

  // 分片直传
  if (init.mode === 'multipart' && init.uploadId && init.partSize && init.partCount) {
    const partSize = init.partSize;
    const totalParts = init.partCount;
    const resume = await loadResume(task.id);
    const doneParts: Record<number, string> =
      resume && resume.nodeId === init.nodeId ? { ...resume.parts } : {};

    let uploadedBytes = 0;
    for (let p = 1; p <= totalParts; p++) {
      if (doneParts[p]) uploadedBytes += Math.min(partSize, file.size - (p - 1) * partSize);
    }

    const record: ResumeRecord = {
      id: task.id, nodeId: init.nodeId, uploadId: init.uploadId,
      fileName: file.name, fileSize: file.size, lastModified: file.lastModified,
      parts: doneParts,
    };

    let lastT = Date.now();
    let lastBytes = uploadedBytes;

    const reportProgress = () => {
      const now = Date.now();
      if (now - lastT > 500) {
        const speed = (uploadedBytes - lastBytes) / ((now - lastT) / 1000);
        lastBytes = uploadedBytes; lastT = now;
        onProgress(Math.min(99, Math.round((uploadedBytes / file.size) * 100)), speed);
      }
    };

    const numbers: number[] = [];
    for (let p = 1; p <= totalParts; p++) if (!doneParts[p]) numbers.push(p);

    let cursor = 0;
    const worker = async () => {
      while (cursor < numbers.length) {
        const partNumber = numbers[cursor++];
        const start = (partNumber - 1) * partSize;
        const end = Math.min(start + partSize, file.size);
        const blob = file.slice(start, end);
        const { url } = await api.upload.signPart({ nodeId: init.nodeId, uploadId: init.uploadId!, partNumber });
        const etag = await putPart(url, blob);
        doneParts[partNumber] = etag;
        uploadedBytes += blob.size;
        record.parts = { ...doneParts };
        await saveResume(record);
        reportProgress();
      }
    };

    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, numbers.length || 1) }, worker));

    const parts = Object.entries(doneParts).map(([pn, etag]) => ({ partNumber: Number(pn), etag }));
    await api.upload.complete({ nodeId: init.nodeId, uploadId: init.uploadId, parts });
    await clearResume(task.id);
    onProgress(100, 0);
  }
}

interface UploaderState {
  tasks: UploadTask[];
  addTasks: (files: FileList | File[], parentId: string | null) => void;
  removeTask: (id: string) => void;
  retryTask: (id: string) => void;
  clearCompleted: () => void;
}

async function runTask(task: UploadTask, update: (patch: Partial<UploadTask>) => void) {
  update({ status: 'uploading', progress: 0, error: undefined });
  try {
    const init = await api.upload.init({
      filename: task.file.name,
      mimeType: task.file.type || 'application/octet-stream',
      size: task.file.size,
      parentId: task.parentId,
    });
    await uploadWithInit(task, init, (pct, speed) => update({ progress: pct, speed }));
    update({ status: 'completed', progress: 100 });
    toast.success(`已上传 ${task.file.name}`);
    // 刷新配额用量
    try {
      const { useAuth } = await import('./useAuth');
      void useAuth.getState().refreshUser();
    } catch { /* ignore */ }
  } catch (err: any) {
    update({ status: 'error', error: err?.message || '上传失败' });
    toast.error(`${task.file.name} 上传失败`);
  }
}

export const useUploader = create<UploaderState>((set, get) => ({
  tasks: [],

  addTasks: (files, parentId) => {
    const list = Array.from(files);
    const newTasks: UploadTask[] = list.map((file) => ({
      id: crypto.randomUUID(), file, parentId, progress: 0, status: 'pending',
    }));
    set((s) => ({ tasks: [...s.tasks, ...newTasks] }));
    newTasks.forEach((task) => {
      const update = (patch: Partial<UploadTask>) =>
        set((s) => ({ tasks: s.tasks.map((t) => (t.id === task.id ? { ...t, ...patch } : t)) }));
      void runTask(task, update);
    });
  },

  removeTask: (id) => {
    void clearResume(id);
    set((s) => ({ tasks: s.tasks.filter((t) => t.id !== id) }));
  },

  retryTask: (id) => {
    const task = get().tasks.find((t) => t.id === id);
    if (!task) return;
    const update = (patch: Partial<UploadTask>) =>
      set((s) => ({ tasks: s.tasks.map((t) => (t.id === id ? { ...t, ...patch } : t)) }));
    void runTask(task, update);
  },

  clearCompleted: () => set((s) => ({ tasks: s.tasks.filter((t) => t.status !== 'completed') })),
}));
